import { toast } from "sonner";
import { useGlobalContext } from "../GlobalContext";

export default function usePackkySetting() {
  const { api, AdminId } = useGlobalContext();

  const shouldShowLocalError = (data) => {
    return (
      data?.responseCode !== "00" &&
      data?.responseCode !== "02" &&
      data?.responseCode !== "03" &&
      data?.responseCode !== "98" &&
      data?.responseCode !== "401"
    );
  };

  const getAdminId = () => {
    if (AdminId) return String(AdminId);

    try {
      const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
      return (
        userData?.adminMeta?.adminId ||
        userData?.adminId ||
        userData?.userid ||
        ""
      );
    } catch {
      return "";
    }
  };

  // --------- 1) FETCH OFFERS ----------
  const fetchOffersApi = async (setOffers, setLoading, silent = false) => {
    if (!silent) setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/getAllDriverOffers", {
        userid: "",
      });

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.response)
          ? res.data.response
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];

        setOffers(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to fetch offers");
      }

      setOffers([]);
      return [];
    } catch (error) {
      console.error("fetchOffersApi error:", error);
      toast.error("Error fetching offers");
      setOffers([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 2) CREATE OFFER ----------
  const createOfferApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/insertDriverOffers", body);

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Offer created");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to create offer");
      }

      return false;
    } catch (error) {
      console.error("createOfferApi error:", error);
      toast.error("Error creating offer");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 3) DELETE OFFER ----------
  const deleteOfferApi = async (offerId, setLoading, onSuccess) => {
    if (!offerId) {
      toast.error("Invalid offer ID");
      return false;
    }

    setLoading(true);

    try {
      const res = await api.post("/api/Driver/v1.0/deleteDriverOffers", {
        offerid: String(offerId),
      });

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Offer deleted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to delete offer");
      }

      return false;
    } catch (error) {
      console.error("deleteOfferApi error:", error);
      toast.error("Error deleting offer");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 4) ADD INCENTIVE ----------
  const addIncentiveApi = async (body, setLoading, onSuccess) => {
    setLoading(true);

    try {
      const res = await api.post(
        "/api/Driver/v1.0/insertDriverOffersIncentive",
        body
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Incentive added");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(res?.data?.responseMessage || "Failed to add incentive");
      }

      return false;
    } catch (error) {
      console.error("addIncentiveApi error:", error);
      toast.error("Error adding incentive");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 5) FETCH PERFORMANCE POINTS BONUS ----------
  const fetchPerformancePointsBonusApi = async (
    setBonusList,
    setLoading,
    silent = false
  ) => {
    if (!silent) setLoading(true);

    try {
      const payload = {
        userId: String(getAdminId()),
      };

      const res = await api.post(
        "/api/Admin/v1.0/getdriverPerformmancePointsBonus",
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        const list = Array.isArray(res.data.response)
          ? res.data.response
          : Array.isArray(res.data.data)
          ? res.data.data
          : [];

        setBonusList(list);
        return list;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to fetch performance bonus"
        );
      }

      setBonusList([]);
      return [];
    } catch (error) {
      console.error("fetchPerformancePointsBonusApi error:", error);
      toast.error("Error fetching performance bonus");
      setBonusList([]);
      return [];
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // --------- 6) INSERT PERFORMANCE POINTS BONUS ----------
  const insertPerformancePointsBonusApi = async (
    body,
    setLoading,
    onSuccess
  ) => {
    setLoading(true);

    try {
      const payload = {
        adminId: String(body?.adminId || getAdminId()),
        fromPoints: String(body?.fromPoints || ""),
        toPoints: String(body?.toPoints || ""),
        bonus: String(body?.bonus || ""),
        status: String(body?.status || "Y"),
      };

      const res = await api.post(
        "/api/Admin/v1.0/insertPerformmancePointsBonus",
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Bonus inserted");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to insert performance bonus"
        );
      }

      return false;
    } catch (error) {
      console.error("insertPerformancePointsBonusApi error:", error);
      toast.error("Error inserting performance bonus");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // --------- 7) UPDATE PERFORMANCE POINTS BONUS STATUS ----------
  const updatePerformancePointsBonusApi = async (
    body,
    setLoading,
    onSuccess
  ) => {
    setLoading(true);

    try {
      const payload = {
        adminId: String(body?.adminId || getAdminId()),
        bonusId: String(body?.bonusId || ""),
        status: String(body?.status || "N"),
      };

      const res = await api.post(
        "/api/Admin/v1.0/updatePerformmancePointsBonus",
        payload,
        {
          headers: {
            accept: "*/*",
            "Content-Type": "application/json-patch+json",
          },
        }
      );

      if (res?.data?.responseCode === "00") {
        toast.success(res?.data?.responseMessage || "Bonus updated");

        if (typeof onSuccess === "function") {
          await onSuccess();
        }

        return true;
      }

      if (shouldShowLocalError(res?.data)) {
        toast.error(
          res?.data?.responseMessage || "Failed to update performance bonus"
        );
      }

      return false;
    } catch (error) {
      console.error("updatePerformancePointsBonusApi error:", error);
      toast.error("Error updating performance bonus");
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    fetchOffersApi,
    createOfferApi,
    deleteOfferApi,
    addIncentiveApi,
    fetchPerformancePointsBonusApi,
    insertPerformancePointsBonusApi,
    updatePerformancePointsBonusApi,
  };
}